export type ScoredSessionType =
  | "practice"
  | "benchmark"
  | "assessment"
  | "game";

export type NormalizedInputType =
  | "insertText"
  | "insertCompositionText"
  | "deleteBackward"
  | "deleteForward"
  | "insertFromPaste"
  | "compositionStart"
  | "compositionUpdate"
  | "compositionEnd"
  | "other";

export interface KeystrokeEvent {
  sessionId: string;
  sequence: number;
  timestampMs: number;
  activeElapsedMs: number;
  inputType: NormalizedInputType;
  expected: string | null;
  actual: string | null;
  sourceIndex: number;
  isCorrectAtEntry: boolean | null;
  isCorrection: boolean;
  wasPreviouslyIncorrect: boolean;
  contentTokenId?: string;
}

export interface MeasurementConfig {
  provisionalMetricsAfterMs: number;
  practiceMinimumDurationMs: number;
  practiceMinimumAttempts: number;
  benchmarkMinimumDurationMs: number;
  benchmarkMinimumAttempts: number;
  plausibleWpmCeiling: number;
  idleThresholdMs: number;
  idleGraceMs: number;
  timingDiscontinuityMs: number;
  consistencyWindowMs: number;
  consistencyStepMs: number;
  consistencyMinimumSamples: number;
}

export const DEFAULT_MEASUREMENT_CONFIG: MeasurementConfig = {
  provisionalMetricsAfterMs: 2_000,
  practiceMinimumDurationMs: 15_000,
  practiceMinimumAttempts: 50,
  benchmarkMinimumDurationMs: 30_000,
  benchmarkMinimumAttempts: 100,
  plausibleWpmCeiling: 300,
  idleThresholdMs: 5_000,
  idleGraceMs: 1_000,
  timingDiscontinuityMs: 4 * 60 * 60 * 1_000,
  consistencyWindowMs: 5_000,
  consistencyStepMs: 1_000,
  consistencyMinimumSamples: 5,
};

export interface SessionValidity {
  isValidPractice: boolean;
  isValidBenchmark: boolean;
  isVerified: boolean;
  reasons: string[];
  pasteDetected: boolean;
  timingDiscontinuityDetected: boolean;
  minimumDurationMet: boolean;
  minimumAttemptsMet: boolean;
  plausibleSpeed: boolean;
}

export interface TypingMetricSnapshot {
  activeDurationMs: number;
  printableAttempts: number;
  correctPrintableAttempts: number;
  initialErrors: number;
  correctedInitialErrors: number;
  uncorrectedErrors: number;
  rawWpm: number | null;
  accurateWpm: number | null;
  netWpm: number | null;
  keystrokeAccuracy: number | null;
  finalTextAccuracy: number | null;
  consistency: number | null;
  isProvisional: boolean;
  validity: SessionValidity;
}

export interface TypingSessionSummary extends TypingMetricSnapshot {
  id: string;
  schemaVersion: 1;
  sessionType: ScoredSessionType;
  startedAt: string | null;
  completedAt: string | null;
  pausedDurationMs: number;
  excludedIdleDurationMs: number;
  sourceLength: number;
  finalTypedLength: number;
  completed: boolean;
  measurementVersion: "2.0.0";
}

export interface SessionValidityInput {
  sessionType: ScoredSessionType;
  activeDurationMs: number;
  printableAttempts: number;
  accurateWpm: number | null;
  pasteDetected: boolean;
  timingDiscontinuityDetected: boolean;
  contentApproved: boolean;
  completed: boolean;
  config: MeasurementConfig;
}

export interface TypingSessionOptions {
  id?: string;
  sourceText: string;
  sessionType?: ScoredSessionType;
  contentApproved?: boolean;
  minimumDurationMs?: number;
  minimumAttempts?: number;
  config?: Partial<MeasurementConfig>;
  clock?: () => number;
  wallClock?: () => number;
}
